import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMaintenanceContext } from '../../features/system/maintenance/contexts/MaintenanceContext';

interface BottomNavigationProps {
    currentScreen: string;
}

// SVG 아이콘 컴포넌트들
const HomeIcon: React.FC<{ active: boolean }> = ({ active }) => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z"
            fill={active ? '#05CD82' : 'none'}
            stroke={active ? '#05CD82' : '#9CA3AF'}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M9 22V12H15V22"
            stroke={active ? '#ffffff' : '#9CA3AF'}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const DeliveryIcon: React.FC<{ active: boolean }> = ({ active }) => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M19 17H22V13L20 9H15V17H17M17 17C17 18.1046 17.8954 19 19 19C20.1046 19 21 18.1046 21 17M17 17C17 15.8954 17.8954 15 19 15C20.1046 15 21 15.8954 21 17"
            stroke={active ? '#3B82F6' : '#9CA3AF'}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M14 17H15M1 17H2M2 17V6C2 5.44772 2.44772 5 3 5H14V17M2 17C2 18.1046 2.89543 19 4 19C5.10457 19 6 18.1046 6 17M2 17C2 15.8954 2.89543 15 4 15C5.10457 15 6 15.8954 6 17M6 17H11M11 17C11 18.1046 11.8954 19 13 19C14.1046 19 15 18.1046 15 17M11 17C11 15.8954 11.8954 15 13 15C14.1046 15 15 15.8954 15 17"
            stroke={active ? '#3B82F6' : '#9CA3AF'}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        {active && (
            <circle cx="4" cy="17" r="2" fill="#3B82F6"/>
        )}
        {active && (
            <circle cx="13" cy="17" r="2" fill="#3B82F6"/>
        )}
    </svg>
);

const SettlementIcon: React.FC<{ active: boolean }> = ({ active }) => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect
            x="3" y="3" width="18" height="18" rx="2"
            fill={active ? '#8B5CF6' : 'none'}
            stroke={active ? '#8B5CF6' : '#9CA3AF'}
            strokeWidth="2"
        />
        <path
            d="M8 12H16M8 8H16M8 16H12"
            stroke={active ? '#ffffff' : '#9CA3AF'}
            strokeWidth="2"
            strokeLinecap="round"
        />
    </svg>
);

const WithdrawalIcon: React.FC<{ active: boolean }> = ({ active }) => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle
            cx="12" cy="12" r="9"
            fill={active ? '#F59E0B' : 'none'}
            stroke={active ? '#F59E0B' : '#9CA3AF'}
            strokeWidth="2"
        />
        <text
            x="12" y="16"
            textAnchor="middle"
            fontSize="11"
            fontWeight="bold"
            fill={active ? '#ffffff' : '#9CA3AF'}
        >
            ₩
        </text>
    </svg>
);

const DeductionIcon: React.FC<{ active: boolean }> = ({ active }) => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle
            cx="12" cy="12" r="9"
            fill={active ? '#EF4444' : 'none'}
            stroke={active ? '#EF4444' : '#9CA3AF'}
            strokeWidth="2"
        />
        <path
            d="M8 12H16"
            stroke={active ? '#ffffff' : '#9CA3AF'}
            strokeWidth="2.5"
            strokeLinecap="round"
        />
    </svg>
);

const BottomNavigation: React.FC<BottomNavigationProps> = ({ currentScreen }) => {
    const navigate = useNavigate();
    const { isDirectSupplier } = useAuth();
    const { isMaintenance } = useMaintenanceContext();

    const navItems = [
        { id: 'delivery', label: '배달', path: '/delivery?tab=summary', Icon: DeliveryIcon },
        { id: 'settlement', label: '정산', path: '/settlement', Icon: SettlementIcon, requiresDirect: true, disabledDuringMaintenance: true },
        { id: 'withdrawal', label: '출금', path: '/withdrawal', Icon: WithdrawalIcon, requiresDirect: true, disabledDuringMaintenance: true },
        { id: 'deduction', label: '일차감', path: '/deduction', Icon: DeductionIcon, requiresDirect: true, disabledDuringMaintenance: true }
    ];

    const handleNavClick = (path: string, requiresDirect?: boolean, disabledDuringMaintenance?: boolean) => {
        // 가맹 협력사인 경우 직영 전용 메뉴 클릭 차단
        if (isDirectSupplier === false && requiresDirect) {
            return;
        }
        // 점검 중일 때 금융 거래 메뉴 클릭 차단
        if (isMaintenance && disabledDuringMaintenance) {
            return;
        }
        navigate(path);
    };

    return (
        <div className="bottom-nav">
            {navItems.map((item) => {
                const isActive = currentScreen === item.id;
                const isDisabledBySupplierType = isDirectSupplier === false && item.requiresDirect;
                const isDisabledByMaintenance = isMaintenance && item.disabledDuringMaintenance;
                const isDisabled = isDisabledBySupplierType || isDisabledByMaintenance;
                
                return (
                    <button
                        key={item.id}
                        className={`nav-item ${isActive ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                        onClick={() => handleNavClick(item.path, item.requiresDirect, item.disabledDuringMaintenance)}
                        disabled={isDisabled}
                    >
                        <item.Icon active={isActive && !isDisabled} />
                        <span>{item.label}</span>
                    </button>
                );
            })}
        </div>
    );
};

export default BottomNavigation;
